<script setup lang="ts">
import { MarAppShell, MarSidebarItem, MarSidebarEdit, MarListGroup, MarIconBtn, MarAvatar, getOrganicSeed, MarModals, MarLoadingSpinner, openModal, useViewport, type IconType } from '@nimling/components-nimtech'
import { SamTabBar, SamTabBarAction, type SamTabBarItem } from '@nimling/components-nimtech/sam'
import SidebarDialog from '@/components/dialogs/SidebarDialog.vue'
import SidebarGroupDialog from '@/components/dialogs/SidebarGroupDialog.vue'
import SearchDialog from '@/components/dialogs/SearchDialog.vue'
import PageTreeItem from '@/components/PageTreeItem.vue'

import { RouterView, useRoute, useRouter } from 'vue-router'
import { ref, computed, inject, watch, onMounted, onBeforeUnmount, type Ref } from 'vue'

import type { Page } from '@/types'
import { useMeStore } from '@/stores/me'
import { useSourcesStore } from '@/stores/sources'
import { MOBILE_TAB_OPTIONS, useUiStore } from '@/stores/ui'
import { useNotificationsStore } from '@/stores/notifications'

interface SidebarNavItem {
  label: string
  icon: string
  route: string
  exact?: boolean
  activeBase?: string
}

const isAppReady = inject<Ref<boolean>>('isAppReady')

const route = useRoute()
const router = useRouter()

const meStore = useMeStore()
const sourcesStore = useSourcesStore()
const uiStore = useUiStore()
const notificationsStore = useNotificationsStore()

const { mobile } = useViewport()
const collapsed = ref(mobile.value || uiStore.sidebarCollapsed)

watch(mobile, (value) => {
  collapsed.value = value || uiStore.sidebarCollapsed
})

onMounted(() => {
  if (!sourcesStore.loaded) sourcesStore.getSources().catch(() => undefined)
})

watch(collapsed, (value) => {
  if (mobile.value) return
  uiStore.setSidebarCollapsed(value)
})

const fullName = computed(() =>
  [meStore.me?.first_name, meStore.me?.last_name].filter(Boolean).join(' '),
)

const email = computed(() => meStore.me?.email ?? '')

const sidebarAvatarSeed = getOrganicSeed()

const navItems = computed<SidebarNavItem[]>(() => [
  { label: 'Views', icon: 'eye', route: '/views', activeBase: '/views' },
  { label: 'Sources', icon: 'database', route: '/sources', activeBase: '/sources' },
  { label: 'Alerts', icon: 'triangle-alert', route: '/alerts', activeBase: '/alerts' },
  { label: 'Annotations', icon: 'flag', route: '/annotations', activeBase: '/annotations' },
  { label: 'Telemetry', icon: 'activity', route: '/telemetry', activeBase: '/telemetry' },
])

const menuItems: SidebarNavItem[] = [
  { label: 'Pages', icon: 'dashboard', route: '/pages', activeBase: '/pages' },
  { label: 'Log streams', icon: 'list', route: '/streams', activeBase: '/streams' },
  { label: 'Alerts', icon: 'triangle-alert', route: '/alerts', activeBase: '/alerts' },
  { label: 'Views', icon: 'eye', route: '/views', activeBase: '/views' },
  { label: 'Sources', icon: 'database', route: '/sources', activeBase: '/sources' },
  { label: 'Annotations', icon: 'flag', route: '/annotations', activeBase: '/annotations' },
  { label: 'Telemetry', icon: 'activity', route: '/telemetry', activeBase: '/telemetry' },
]

const settingsItem: SidebarNavItem = { label: 'Settings', icon: 'settings', route: '/settings', activeBase: '/settings' }

const streamItems = computed(() =>
  sourcesStore.sources.map((source) => ({ id: source.id, label: source.name, route: `/streams/${source.id}` })),
)

const editSidebar = () => openModal({ kind: 'dialog', component: SidebarDialog })

const postSidebarGroup = () => openModal({ kind: 'dialog', component: SidebarGroupDialog })

const SEARCH_TOP = 0.12
const searchHint = navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl K'
const searchOpen = ref(false)

const openSearch = () => {
  if (searchOpen.value) return
  searchOpen.value = true
  if (mobile.value) collapsed.value = true
  void openModal({ kind: 'dialog', component: SearchDialog, position: { top: Math.round(window.innerHeight * SEARCH_TOP) } }).then(() => {
    searchOpen.value = false
  })
}

const onSearchKey = (event: KeyboardEvent) => {
  if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return
  event.preventDefault()
  openSearch()
}
onMounted(() => window.addEventListener('keydown', onSearchKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onSearchKey))

const isNavActive = (item: SidebarNavItem) => {
  if (item.exact) return route.path === item.route
  if (!item.activeBase) return false
  return route.path === item.activeBase || route.path.startsWith(`${item.activeBase}/`)
}

const onNavClick = (event: MouseEvent, path: string) => {
  event.preventDefault()
  router.push(path)
}

const navigate = (path: string) => router.push(path)

const unreadBadge = computed(() => (notificationsStore.unread ? (notificationsStore.unread > 99 ? '99+' : notificationsStore.unread) : undefined))

const tabs = computed<SamTabBarItem[]>(() =>
  uiStore.mobileTabs
    .map((key) => MOBILE_TAB_OPTIONS.find((tab) => tab.key === key))
    .filter((tab) => !!tab)
    .map((tab) => (tab.key === 'notifications' ? { ...tab, badge: unreadBadge.value } : tab)),
)

const activeTab = computed(() => tabs.value.find((tab) => tab.href && (route.path === tab.href || route.path.startsWith(`${tab.href}/`)))?.key ?? null)

const onTab = (tab: SamTabBarItem, event: MouseEvent) => {
  event.preventDefault()
  collapsed.value = true
  if (tab.key === 'search') openSearch()
  if (tab.href) navigate(tab.href)
}

const onProfile = (event: MouseEvent) => {
  event.preventDefault()
  collapsed.value = true
  navigate('/settings')
}

const newPage = (parent: Page | null = null) => router.push(parent ? { path: '/pages/create', query: { parent: parent.id } } : '/pages/create')
</script>

<template>
  <MarModals />

  <MarLoadingSpinner v-if="!isAppReady" mode="page" label="" />

  <MarAppShell
    v-else
    v-model:collapsed="collapsed"
    :split="uiStore.sidebarSplit"
    :split-collapsed="uiStore.sidebarSplitCollapsed"
    @update:split="uiStore.putSidebarSplit"
    @update:split-collapsed="uiStore.putSidebarSplitCollapsed"
  >
    <template #nav>
      <MarListGroup label="Pages">
        <template #actions>
          <MarIconBtn icon="plus" tooltip="Create page" @click.stop="newPage()" />
          <MarIconBtn icon="folder-plus" tooltip="Create section" @click.stop="postSidebarGroup" />
        </template>

        <MarSidebarEdit nest @reorder="uiStore.putSidebarReorder">
          <MarListGroup
            v-for="group in uiStore.sidebarSections"
            :id="group.id"
            :key="group.id"
            :label="group.label"
            :icon="group.icon as IconType"
          >
            <template #actions>
              <MarIconBtn icon="trash" severity="danger" tooltip="Remove section" @click.stop="uiStore.deleteSidebarGroup(group.id)" />
            </template>
            <PageTreeItem v-for="page in group.pages" :key="page.id" :page="page" :tree="uiStore.sidebarPageTree" @create="newPage" />
          </MarListGroup>
          <PageTreeItem v-for="page in uiStore.sidebarPages" :key="page.id" :page="page" :tree="uiStore.sidebarPageTree" @create="newPage" />
        </MarSidebarEdit>
      </MarListGroup>
    </template>

    <template #split>
      <MarListGroup label="Log Streams">
        <MarSidebarItem
          v-for="item in streamItems"
          :key="item.id"
          :label="item.label"
          icon="list"
          :href="item.route"
          :active="route.path === item.route"
          @click="onNavClick($event, item.route)"
        />
      </MarListGroup>
    </template>

    <template #footer>
      <MarSidebarItem
        v-for="item in navItems"
        :key="item.route"
        :label="item.label"
        :icon="item.icon"
        :href="item.route"
        :active="isNavActive(item)"
        @click="onNavClick($event, item.route)"
      />
      <MarSidebarItem label="Search" icon="search" :shortcut="searchHint" @click="openSearch" />
      <MarSidebarItem label="Edit sidebar" icon="sidebar" @click="editSidebar" />
      <MarSidebarItem label="Collapse" icon="chevrons-left" @click="collapsed = true" />
      <MarSidebarItem
        :label="fullName || email || 'User'"
        :detail="email"
        href="/settings"
        :active="isNavActive(settingsItem)"
        actions-visible
        @click="onNavClick($event, '/settings')"
      >
        <template #avatar>
          <MarAvatar :src="meStore.me?.user_image" :label="fullName || email" shape="organic" :seed="sidebarAvatarSeed" scale />
        </template>
        <template #actions>
          <MarIconBtn
            icon="notification"
            tooltip="Notifications"
            :badge="unreadBadge"
            :active="route.path.startsWith('/notifications')"
            @click.stop="navigate('/notifications')"
          />
        </template>
      </MarSidebarItem>
    </template>

    <template #menu>
      <MarSidebarItem
        v-for="item in menuItems"
        :key="item.route"
        :label="item.label"
        :icon="item.icon"
        :href="item.route"
        :active="isNavActive(item)"
        @click="onNavClick($event, item.route)"
      />
      <MarSidebarItem label="Search" icon="search" @click="openSearch" />
      <MarSidebarItem
        :label="settingsItem.label"
        :icon="settingsItem.icon"
        :href="settingsItem.route"
        :active="isNavActive(settingsItem)"
        @click="onNavClick($event, settingsItem.route)"
      />
      <MarSidebarItem
        :label="fullName || email || 'User'"
        :detail="email"
        href="/settings"
        actions-visible
        @click="onNavClick($event, '/settings')"
      >
        <template #avatar>
          <MarAvatar :src="meStore.me?.user_image" :label="fullName || email" shape="organic" :seed="sidebarAvatarSeed" scale />
        </template>
        <template #actions>
          <MarIconBtn
            icon="notification"
            tooltip="Notifications"
            :badge="unreadBadge"
            :active="route.path.startsWith('/notifications')"
            @click.stop="navigate('/notifications')"
          />
        </template>
      </MarSidebarItem>
    </template>

    <RouterView :key="route.path" />

    <template #bar>
      <SamTabBar :items="tabs" :active="activeTab" label="Navigation" @select="onTab">
        <template #trailing>
          <SamTabBarAction label="Profile" icon="user" href="/settings" @click="onProfile" />
        </template>
      </SamTabBar>
    </template>
  </MarAppShell>
</template>

